import { FC, ComponentProps, useMemo, ReactNode } from 'react';
import { Box, Typography, Svg, Button, IconButton } from '@altermaind/web/ui';
import { EmbeddedStyle } from '../EmbeddedStyle';

type Type = 'error' | 'success' | 'warning' | 'primary' | 'transparent' | 'white';

type ConditionalCtaProps =
  | { showCta: true; ctaLabel: string; ctaProps?: Omit<ComponentProps<typeof Button>, 'children'> }
  | { showCta?: false | undefined; ctaLabel?: undefined; ctaProps?: undefined };

type Props = ConditionalCtaProps & {
  label: ReactNode;
  labelProps?: ComponentProps<typeof Typography>;
  type: Type;
  onClick?: () => void;
  dataTestId?: string;
  iconName?: string;
  containerProps?: ComponentProps<typeof Box>;
  closeButton?: boolean;
  closeButtonCallback?: () => void;
};

/**
 * AlertBadge component displays a badge with a label, an optional icon, and a border color based on the type.
 * It can also handle click events if an onClick function is provided.
 *
 * @param {string} label - The text to display inside the badge.
 * @param {string} labelProps - Optional props for the label typography.
 * @param {'error' | 'success' | 'warning' | 'primary'} type - The type of the badge which determines the border color.
 * @param {() => void} [onClick] - Optional click handler function.
 * @param {string} [dataTestId] - Optional data-testid attribute for testing purposes.
 * @param {string} [iconName] - Optional name of the icon to display inside the badge.
 * @param {string} [containerProps] - Optional props for the box container.
 * @param {boolean} [showCta] - Optional flag to show a call-to-action button.
 * @param {string} [ctaLabel] - Optional label for the call-to-action button (required if showCta is true).
 * @param {ComponentProps<typeof Button>} [ctaProps] - Optional props for the call-to-action button.
 *
 * @returns {JSX.Element} The rendered AlertBadge component.
 *
 * @example
 * <AlertBadge
 *   label="Attenzione verifica i dati inseriti"
 *   type="info"
 *   onClick={() => console.log('Badge clicked')}
 *   dataTestId="alert-badge-info"
 *   iconName="info"
 *    containerProps={{ mt: 'xs' }}
 * />
 */
export const AlertBadge: FC<Props> = ({
  containerProps,
  label,
  labelProps = {},
  type,
  onClick,
  showCta,
  ctaLabel,
  ctaProps,
  dataTestId,
  iconName,
  closeButton,
  closeButtonCallback,
}) => {
  const bgMapping = (typeAlert: Type) => {
    switch (typeAlert) {
      case 'error':
        return 'background/error';
      case 'success':
        return 'background/success';
      case 'warning':
        return 'background/warning';
      case 'primary':
        return 'primary';
      case 'transparent':
        return 'transparent';
      case 'white':
        return 'white';
      default:
        return 'primary';
    }
  };

  const svgColor = useMemo(() => {
    if (type === 'primary') {
      return 'white';
    }
    if (type === 'white') {
      return 'grey/dark';
    }
    if (type === 'transparent') {
      return 'primary/text';
    }
    return type;
  }, [type]);

  return (
    <Box
      style={onClick ? { cursor: 'pointer' } : {}}
      onClick={!showCta ? onClick : undefined}
      alignItems='center'
      borderColor={type === 'transparent' ? 'white' : type}
      borderStyle='solid'
      borderWidth='xs'
      borderRadius='s'
      backgroundColor={bgMapping(type)}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...containerProps}
    >
      <Box display='flex' p='xs' alignItems='center'>
        {iconName && (
          <Box mr='xs' mt='xxxxs'>
            <Svg fill={svgColor} name={iconName as string} size='s' />
          </Box>
        )}
        <Box
          flexDirection='row'
          display='flex'
          alignItems='center'
          justifyContent='space-between'
          width='100%'
        >
          <Typography
            data-testid={dataTestId}
            variant='p2/light'
            color={type === 'white' ? 'grey/dark' : 'primary/text'}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...labelProps}
          >
            <EmbeddedStyle>{label}</EmbeddedStyle>
          </Typography>
          {showCta ? (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <Button width='auto' ml='xs' variant='positive' onClick={onClick} {...ctaProps}>
              {ctaLabel}
            </Button>
          ) : null}
        </Box>
        {closeButton && (
          <Box ml='auto' mt='xxxxs'>
            <IconButton height='none' variant='link/primary' onClick={closeButtonCallback}>
              <Svg name='close' />
            </IconButton>
          </Box>
        )}
      </Box>
    </Box>
  );
};
